import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Aperture } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const Signup = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: '',
    company_name: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!formData.role) {
        toast.error('Please select a role');
        return;
      }
      setStep(2);
      return;
    }

    // Validate business fields
    if (formData.role === 'business' && !formData.company_name) {
      toast.error('Please enter your business/organization name');
      return;
    }

    // Validate terms acceptance
    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.role, formData.company_name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const user = await googleLogin(credentialResponse.credential);
      toast.success(user.needs_role ? 'Account created successfully!' : 'Welcome back!');
      if (user.needs_role) {
        navigate('/select-role');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Google sign up failed');
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#f7f5f2' }}>
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-black">
        <img
          src="https://images.pexels.com/photos/29675497/pexels-photo-29675497.jpeg"
          alt="Product photography"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#d4a843' }}>Join Today</p>
          <h2 className="mb-4" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '3.5rem', lineHeight: 1.1 }}>FrameFinder</h2>
          <p className="text-gray-300 text-lg font-light">Start connecting with businesses or showcase your creative talent</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fadeInUp">
          <Link to="/" className="inline-flex items-center gap-2 mb-12 transition-colors group" style={{ color: '#6b6b6b' }}>
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to home</span>
          </Link>

          <h1 className="mb-2" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '2.5rem' }}>Create account</h1>
          <p className="mb-10" style={{ color: '#6b6b6b' }}>
            {step === 1 ? 'Choose how you want to use FrameFinder' : 'Fill in your details to get started'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleRoleSelect('business')}
                    className="p-6 rounded-xl border-2 transition-all text-left"
                    style={{
                      borderColor: formData.role === 'business' ? '#d4a843' : '#e4e1db',
                      background: formData.role === 'business' ? '#fdf8ef' : 'transparent',
                    }}
                    data-testid="role-business-btn"
                  >
                    <Building2 className="w-8 h-8 mb-4" style={{ color: formData.role === 'business' ? '#d4a843' : '#9a9a9a' }} strokeWidth={1.5} />
                    <h3 className="font-semibold mb-1" style={{ color: '#0e0e0e' }}>I am a Business</h3>
                    <p className="text-sm" style={{ color: '#6b6b6b' }}>Looking to hire photographers</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRoleSelect('creative')}
                    className="p-6 rounded-xl border-2 transition-all text-left"
                    style={{
                      borderColor: formData.role === 'creative' ? '#d4a843' : '#e4e1db',
                      background: formData.role === 'creative' ? '#fdf8ef' : 'transparent',
                    }}
                    data-testid="role-creative-btn"
                  >
                    <Aperture className="w-8 h-8 mb-4" style={{ color: formData.role === 'creative' ? '#d4a843' : '#9a9a9a' }} strokeWidth={1.5} />
                    <h3 className="font-semibold mb-1" style={{ color: '#0e0e0e' }}>I am a Photographer</h3>
                    <p className="text-sm" style={{ color: '#6b6b6b' }}>Looking to partner with businesses</p>
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full text-white font-medium"
                  style={{ background: '#0e0e0e' }}
                  disabled={!formData.role}
                  data-testid="continue-btn"
                >
                  Continue
                </Button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: '#e4e1db' }} />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4" style={{ background: '#f7f5f2', color: '#9a9a9a' }}>or</span>
                  </div>
                </div>

                <div className="flex justify-center w-full overflow-hidden">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast.error('Google sign up failed')}
                    useOneTap={false}
                    theme="outline"
                    size="large"
                    text="signup_with"
                    width="100%"
                  />
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 text-sm mb-4 transition-colors hover:underline"
                  style={{ color: '#6b6b6b' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to role selection
                </button>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="signup-name-input"
                    className="h-12 rounded-lg"
                    style={{ borderColor: '#e4e1db' }}
                  />
                </div>

                {formData.role === 'business' && (
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Business/Organization Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      type="text"
                      placeholder="Your Company Inc."
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      data-testid="signup-company-input"
                      className="h-12 rounded-lg"
                      style={{ borderColor: '#e4e1db' }}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="signup-email-input"
                    className="h-12 rounded-lg"
                    style={{ borderColor: '#e4e1db' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    data-testid="signup-password-input"
                    className="h-12 rounded-lg"
                    style={{ borderColor: '#e4e1db' }}
                  />
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={setAcceptedTerms}
                    className="mt-1"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                    I agree to the{' '}
                    <Link
                      to={formData.role === 'creative' ? '/terms/creative' : '/terms/client'}
                      target="_blank"
                      className="underline hover:no-underline"
                      style={{ color: '#0e0e0e' }}
                    >
                      Terms and Conditions
                    </Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full text-white font-medium"
                  style={{ background: '#0e0e0e' }}
                  disabled={loading || !acceptedTerms}
                  data-testid="signup-submit-btn"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </>
            )}
          </form>

          <p className="text-center mt-10" style={{ color: '#6b6b6b' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: '#0e0e0e' }} data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
